import { Combobox } from '@headlessui/react';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import InputBase from '..';
import Spinner from '../../spinner';
import { spinnerSizes } from '../../spinner/spinnerConfig';
import { InputBackgroundType } from '../inputConfig';

/**
 * Typeahead Component
 *
 * A headless UI–powered searchable dropdown that supports both local filtering and remote search,
 * pagination via a “Display more” button, and highlight matching text in labels and subtexts.
 *
 *
 * When to use This Component:
 * - Working with a large datasets that requires lazy loading / pagination
 *   - `onSearch` can be used with a callback to call data from API
 *   - `Display More` can be used with the same manner to paginate data dynamically
 * - Working with a search that requires highlight and sub information
 * - Working with data selection with lot of meta data
 *   - Using the generic values you can simplify the selection
 *
 * String vs Generic Values(<T,>):
 * - Use string when you work with simple selection (e.g: id selections)
 * - Use the Generic Values for complex data (e.g., objects with metadata)
 *   Generic Values can eleminate the needs for searching / filtering data.
 *
 */
export const Typeahead = <T,>({
  options,
  value,
  onSelect,
  onSearch,
  debounceTimeInMs,
  displayMoreHandler,
  displayMoreType = 'DISABLED',
  displayItemCount = 10,
  isLoading = false,
  isOpenInitially = false,
  label,
  assistiveMessage,
  required = false,
  errorMessage,
  backgroundType = 'DARK',
  disabled = false,
  placeHolder,
  onBlur,
  dataTestId,
  title
}: TypeaheadProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const [displayedOptionsCount, setDisplayedOptionsCount] =
    useState(displayItemCount);
  const [openOptions, setOpenOptions] = useState(isOpenInitially);

  /**
   * Ref holding either the debounce wrapper or raw onSearch function
   * We are using useRef to ensure it won't re-render when it is updated
   */
  const searchRef = useRef<(input: string) => void>(undefined);

  /**
   * Debounce Wrapper
   *
   * The debounce wrappper exists because onSearch are oftenly used to trigger network requests
   * having this functionality here will simplify debounce handling else where.
   */
  useEffect(() => {
    if (!onSearch) {
      searchRef.current = undefined;
      return;
    }

    // If debounce is defined we want to adjust the ref
    if (debounceTimeInMs !== undefined) {
      const debouncedFn = debounce(onSearch, debounceTimeInMs);
      searchRef.current = debouncedFn;
      return () => {
        debouncedFn.cancel(); // cleanup
      };
    }

    // no debounce specified, call raw onSearch directrly
    searchRef.current = onSearch;
  }, []);

  const handleInputChange = (input: string) => {
    setInputValue(input);
    if (searchRef.current) {
      searchRef.current(input);
    }
  };

  const filteredOptions = useMemo(() => {
    /**
     * On Remote, render what parent supplied
     */
    if (onSearch) return options;

    /**
     * When there is no input, return the default list
     */
    if (!inputValue || inputValue.length === 0) return options;

    const lower = inputValue.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(lower) ||
        opt.subtext.toLowerCase().includes(lower)
    );
  }, [options, inputValue, onSearch]);

  const hasRemainingItems = filteredOptions.length > displayedOptionsCount;
  const showMore =
    displayMoreType === 'ALWAYS_VISIBLE' ||
    (displayMoreType === 'WHEN_HAVE_REMAINING' && hasRemainingItems);

  return (
    <Combobox
      value={value ?? null}
      onChange={(opt) => {
        if (opt) onSelect(opt);
      }}
    >
      <div
        className="relative w-full"
        /**
         * Handle open on click
         * https://github.com/tailwindlabs/headlessui/discussions/1205
         * It is a known issue to and a fix using the delay is suggested.
         *
         * This issue shuold be fixed when we update headless UI from 1.7 -> 2.1
         */
        onFocus={() => setOpenOptions(true)}
        onBlur={() => setTimeout(() => setOpenOptions(false), 200)}
        data-test-id={`typeahead-${dataTestId ?? title}`}
      >
        <InputBase
          inputComponent={
            <div className="relative w-full">
              <Combobox.Button
                onClick={(e) => e.stopPropagation()}
                className={'w-full'}
              >
                <Combobox.Input
                  className="body-01 w-full px-spacing-02 py-[6px] text-primary outline-none placeholder:text-tertiary bg-transparent disabled:text-tertiary"
                  placeholder={placeHolder}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onBlur={onBlur}
                  displayValue={(opt: TypeaheadOption<T>) => opt?.label ?? ''}
                  autoComplete="off"
                  data-test-id={`typeahead-${dataTestId ?? title}-text-input`}
                />
              </Combobox.Button>
              {isLoading && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <Spinner color="BLUE" size={spinnerSizes.SMALL} />
                </div>
              )}
            </div>
          }
          label={label}
          assistiveMessage={assistiveMessage}
          required={required}
          errorMessage={errorMessage}
          backgroundType={backgroundType}
          disabled={disabled}
          isBeingUpdated={false}
        />

        <Combobox.Options
          className="absolute w-full rounded-radius-02 shadow-lg bg-primary z-dropdown max-h-60 overflow-auto -mt-4"
          static={openOptions}
          data-test-id={`typeahead-${dataTestId ?? title}-dropdown-body`}
        >
          {filteredOptions.length === 0 && (
            <div
              className="px-spacing-04 py-spacing-03 text-small text-secondary cursor-default"
              data-test-id={`typeahead-${dataTestId ?? title}-dropdown-no-item`}
              // Prevents focus loss when clicking on the div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              No items available
            </div>
          )}

          {filteredOptions.length > 0 &&
            filteredOptions
              .slice(0, displayedOptionsCount)
              .map((option, index) => (
                <Combobox.Option
                  key={option.key}
                  value={option}
                  className={({ active }) =>
                    classNames(
                      'cursor-pointer select-none px-spacing-04 py-spacing-03',
                      {
                        'bg-secondary text-primary': active,
                        'text-secondary': !active
                      }
                    )
                  }
                  data-test-id={`typeahead-${dataTestId}-dropdown-item-${option.key}`}
                >
                  {() => (
                    <div
                      data-test-id={`typeahead-${dataTestId ?? title}-dropdown-item-body-${index}`}
                    >
                      <div
                        data-test-id={`typeahead-${dataTestId ?? title}-dropdown-item-content-${index}`}
                      >
                        {getHighlightedText(option.label, inputValue)}
                      </div>
                      <div
                        className="text-tertiary text-xsmall"
                        data-test-id={`typeahead-${dataTestId ?? title}-dropdown-item-sub-content-${index}`}
                      >
                        {getHighlightedText(option.subtext, inputValue)}
                      </div>
                    </div>
                  )}
                </Combobox.Option>
              ))}

          {showMore && (
            <div className="border-t border-secondary">
              <button
                onMouseDown={(e) => e.preventDefault()} // Keeps the focus on the Input so it won't closed
                onClick={(e) => {
                  e.preventDefault();
                  displayMoreHandler?.();
                  setDisplayedOptionsCount((c) => c + displayItemCount);
                }}
                className="block w-full px-spacing-04 py-spacing-03 text-sm text-blue-600 hover:text-blue-700 active:text-blue-800 focus:outline-none"
              >
                Display more
              </button>
            </div>
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  );
};

export type TypeaheadProps<T> = {
  title?: string;
  options: TypeaheadOption<T>[];
  value?: TypeaheadOption<T>;
  debounceTimeInMs?: number;
  onSelect: (selected: TypeaheadOption<T>) => void;
  onSearch?: (inputValue: string) => void;
  displayMoreHandler?: () => void;
  displayMoreType?: 'ALWAYS_VISIBLE' | 'WHEN_HAVE_REMAINING' | 'DISABLED';
  displayItemCount?: number;
  isLoading?: boolean;
  isOpenInitially?: boolean;

  // InputBase props
  label?: string;
  placeHolder: string;
  assistiveMessage?: string;
  required?: boolean;
  errorMessage?: string | null;
  backgroundType?: InputBackgroundType;
  disabled?: boolean;
  onBlur?: () => void;
  dataTestId?: string;
};

export type TypeaheadOption<T> = {
  key: string;
  label: string;
  subtext: string;
  value: T;
};

// Utility for highlighting matches
const getHighlightedText = (text: string, highlight: string) => {
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <b key={i}>{part}</b>
        ) : (
          part
        )
      )}
    </span>
  );
};
